import { useEffect, useRef, useState } from 'react';
import type { DrawerInfo } from '../../state/drawer.js';
import { BackIcon, MenuSmallIcon, MoreIcon } from '../icons.js';

interface TopBarProps {
  onToggleSidebar: () => void;
  title: string;
  actions: DrawerInfo['actions'];
}

/** The mobile top bar: menu button, title and the "more" menu with the drawer actions. An action with a
 *  submenu (Write...) opens it in place of the list, with a Back row to return. */
export function TopBar({ onToggleSidebar, title, actions }: TopBarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [submenuOf, setSubmenuOf] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    const close = (e: MouseEvent): void => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) setSubmenuOf(null);
  }, [moreOpen]);

  const open = submenuOf ? actions?.find((a) => a.label === submenuOf && a.submenu && !a.disabled) : undefined;

  return (
    <div className="top-bar">
      <button
        type="button"
        className="icon-btn menu-btn"
        aria-label="Open menu"
        onClick={onToggleSidebar}
      >
        <MenuSmallIcon />
      </button>
      <span className="top-bar-title">{title}</span>
      {actions && actions.length > 0 ? (
        <div className="top-bar-actions">
          <div className="more-menu" ref={moreRef}>
            <button
              type="button"
              className="icon-btn"
              aria-label="More"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((o) => !o)}
            >
              <MoreIcon />
            </button>
            <div className={`menu ${moreOpen ? 'open' : ''}`} role="menu" aria-label={open ? open.label.replace(/\.+$|…$/, '') : 'More'}>
              {open?.submenu ? (
                <>
                  <button type="button" className="menu-item menu-back" role="menuitem" onClick={() => setSubmenuOf(null)}>
                    <span className="menu-back-row">
                      <BackIcon size={14} />
                      <span>{open.label}</span>
                    </span>
                  </button>
                  {open.submenu.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      className="menu-item"
                      role="menuitem"
                      onClick={() => {
                        setMoreOpen(false);
                        item.onSelect();
                      }}
                    >
                      <span className="menu-title">{item.label}</span>
                      {item.description ? <span className="muted tiny">{item.description}</span> : null}
                    </button>
                  ))}
                </>
              ) : (
                actions.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    className="menu-item"
                    role="menuitem"
                    disabled={action.disabled}
                    aria-haspopup={action.submenu ? 'menu' : undefined}
                    onClick={() => {
                      if (action.submenu) {
                        setSubmenuOf(action.label);
                        return;
                      }
                      setMoreOpen(false);
                      action.onSelect?.();
                    }}
                  >
                    <span>{action.label}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
